import { CrmEntityType, Prisma, PrismaClient } from '@prisma/client';
import { uniqueChunks } from './chunks';
import { CrmSyncRecordClient, CrmSyncRecordRawClient } from './types';

/** Raw upsert because Prisma's MySQL upsert is check-then-write and can race concurrent requests. */
export async function upsertContactRecord({
  crmObjectId,
  entityId,
  now,
  prisma,
}: {
  prisma: CrmSyncRecordRawClient;
  entityId: string;
  crmObjectId: string;
  now: Date;
}): Promise<void> {
  await prisma.$executeRaw(Prisma.sql`
    INSERT INTO CrmSyncRecord (entityType, entityId, crmObjectId, dueAt, createdAt, updatedAt)
    VALUES (${CrmEntityType.CONTACT}, ${entityId}, ${crmObjectId}, ${now}, ${now}, ${now})
    ON DUPLICATE KEY UPDATE
      crmObjectId = VALUES(crmObjectId),
      updatedAt = VALUES(updatedAt)
  `);
}

/** Raw INSERT IGNORE returning whether this request won the company provisioning claim. */
export async function claimCompanyProvisioning({
  handoffAt,
  now,
  prisma,
  userId,
}: {
  prisma: CrmSyncRecordRawClient;
  userId: string;
  now: Date;
  handoffAt: Date;
}): Promise<boolean> {
  const inserted = await prisma.$executeRaw(Prisma.sql`
    INSERT IGNORE INTO CrmSyncRecord (entityType, entityId, createPendingAt, dueAt, createdAt, updatedAt)
    VALUES (${CrmEntityType.COMPANY}, ${userId}, ${now}, ${handoffAt}, ${now}, ${now})
  `);

  return inserted > 0;
}

/** Resolves the pending company create and queues its contacts in one transaction. */
export async function completeCompanyProvisioning({
  crmObjectId,
  now,
  prisma,
  userId,
}: {
  prisma: Pick<PrismaClient, '$transaction'>;
  userId: string;
  crmObjectId: string;
  now: Date;
}): Promise<void> {
  await prisma.$transaction(async tx => {
    await tx.crmSyncRecord.updateMany({
      data: { createPendingAt: null, crmObjectId, dueAt: now },
      where: { entityId: userId, entityType: CrmEntityType.COMPANY },
    });
    await tx.crmSyncRecord.updateMany({
      data: { dueAt: now },
      where: { entityId: userId, entityType: CrmEntityType.CONTACT },
    });
  });
}

/** Explicit user/admin requests may intentionally pre-empt an existing backoff. */
export async function requestEarlySync({
  entityIds,
  entityTypes,
  now,
  prisma,
}: {
  prisma: CrmSyncRecordClient;
  entityIds: string[];
  entityTypes: CrmEntityType[];
  now: Date;
}): Promise<number> {
  const uniqueEntityTypes = [...new Set(entityTypes)];
  if (uniqueEntityTypes.length === 0) return 0;

  let count = 0;
  for (const ids of uniqueChunks(entityIds)) {
    const result = await prisma.crmSyncRecord.updateMany({
      data: { dueAt: now },
      where: { entityId: { in: ids }, entityType: { in: uniqueEntityTypes } },
    });
    count += result.count;
  }

  return count;
}

export async function readCrmLinks({
  prisma,
  userId,
}: {
  prisma: CrmSyncRecordClient;
  userId: string;
}): Promise<{ companyId: string | null; contactId: string | null }> {
  const records = await prisma.crmSyncRecord.findMany({
    select: { crmObjectId: true, entityType: true },
    where: {
      entityId: userId,
      entityType: { in: [CrmEntityType.CONTACT, CrmEntityType.COMPANY] },
    },
  });

  return {
    companyId: records.find(record => record.entityType === CrmEntityType.COMPANY)?.crmObjectId ?? null,
    contactId: records.find(record => record.entityType === CrmEntityType.CONTACT)?.crmObjectId ?? null,
  };
}
